import { AuthAPI, AuthResponse } from "./AuthAPI"
import {
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken
} from "./TokenStore"
import { HttpError } from "../api/ApiClient"

export enum AuthState {
  UNINITIALIZED = "UNINITIALIZED",
  AUTHENTICATED = "AUTHENTICATED",
  REFRESHING = "REFRESHING",
  UNAUTHENTICATED = "UNAUTHENTICATED",
  ERROR = "ERROR"
}

type Listener = (state: AuthState) => void

export class AuthManager {
  private state: AuthState = AuthState.UNINITIALIZED
  private accessToken: string | null = null
  private refreshPromise: Promise<void> | null = null
  private isInitialized = false // 🔥 КРИТИЧНО

  private listeners = new Set<Listener>()
  private api: AuthAPI

  constructor(baseUrl: string) {
    this.api = new AuthAPI(baseUrl)
  }

  // ---------------------------
  // Public API
  // ---------------------------

  getState(): AuthState {
    return this.state
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getAccessToken(): string | null {
    return this.accessToken
  }

  // 🔥 ВАЖНО: ожидание готовности auth
  async waitUntilReady(): Promise<void> {
    if (this.isInitialized) return

    await new Promise<void>((resolve) => {
      const check = () => {
        if (this.isInitialized) {
          resolve()
        } else {
          setTimeout(check, 10)
        }
      }
      check()
    })
  }

  // ---------------------------
  // INIT (Startup flow)
  // ---------------------------

  async init(): Promise<void> {
    const refreshToken = await getRefreshToken()

    if (!refreshToken) {
      this.setState(AuthState.UNAUTHENTICATED)
      this.isInitialized = true
      return
    }

    try {
      await this.performRefresh(refreshToken)
      this.setState(AuthState.AUTHENTICATED)
    } catch {
      await clearRefreshToken()
      this.setState(AuthState.UNAUTHENTICATED)
    }

    this.isInitialized = true
  }

  // ---------------------------
  // LOGIN / REGISTER
  // ---------------------------

  async login(email: string, password: string): Promise<void> {
    const result = await this.api.login(email, password)
    await this.handleAuthSuccess(result)
  }

  async requestOtp(email: string): Promise<void> {
    await this.api.requestOtp(email)
    this.setState(AuthState.UNAUTHENTICATED)
  }

  async confirmOtp(email: string, otp: string): Promise<void> {
    const result = await this.api.confirmOtp(email, otp)
    await this.handleAuthSuccess(result)
  }


  // ---------------------------
  // LOGOUT
  // ---------------------------

  async logout(): Promise<void> {
    const refreshToken = await getRefreshToken()

    try {
      if (this.accessToken && refreshToken) {
        await this.api.logout(this.accessToken, refreshToken)
      }
    } catch {
      // ignore
    }

    this.accessToken = null
    await clearRefreshToken()
    this.setState(AuthState.UNAUTHENTICATED)
  }

  // ---------------------------
  // 401 Handler
  // ---------------------------

  async handle401(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    const refreshToken = await getRefreshToken()
    if (!refreshToken) {
      await this.logout()
      return
    }

    this.refreshPromise = this.performRefresh(refreshToken)
      .then(() => {
        this.setState(AuthState.AUTHENTICATED)
      })
      .catch(async () => {
        await this.logout()
      })
      .finally(() => {
        this.refreshPromise = null
      })

    return this.refreshPromise
  }

  async waitUntilAuthenticated(): Promise<void> {
    if (this.accessToken) return

    const refreshToken = await getRefreshToken()
    if (!refreshToken) {
      throw new HttpError(401)
    }

    if (this.refreshPromise) {
      await this.refreshPromise
    } else {
      await this.handle401()
    }

    // 🔥 ВОТ ЭТО КРИТИЧНО
    if (!this.accessToken) {
      throw new HttpError(401)
    }
  }
  

  // ---------------------------
  // INTERNAL
  // ---------------------------

  private async performRefresh(refreshToken: string): Promise<void> {
    this.setState(AuthState.REFRESHING)

    const result = await this.api.refresh(refreshToken)

    this.accessToken = result.access_token
    await setRefreshToken(result.refresh_token)
  }

  private async handleAuthSuccess(result: AuthResponse): Promise<void> {
    this.accessToken = result.access_token
    await setRefreshToken(result.refresh_token)
    this.setState(AuthState.AUTHENTICATED)
  }

  private setState(newState: AuthState): void {
    this.state = newState
    this.listeners.forEach(l => l(newState))
  }



}